/* eslint-disable @typescript-eslint/no-explicit-any */
import { getErrorMessage } from '@juvare/widget-ui/Utils';
/**
 * Builds an event editor tab configuration for a widget form.
 *
 * @param {WidgetProps<any, any>} props - The widget props used to build the tab.
 * @param {object} [params] - Optional parameters for customizing the tab.
 * @param {boolean} [params.eventOnLoad] - Include an `onLoad` JavaScript event function.
 * @param {boolean} [params.eventOnChange] - Include an `onChange` JavaScript event function.
 * @param {boolean} [params.eventOnChangeTab] - Include an `onChangeTab` JavaScript event function.
 * @param {boolean} [params.eventOnClick] - Include an `onClick` JavaScript event function.
 * @param {boolean} [params.autoSave] - Include an auto-save checkbox.
 * @param {DefaultFormWidgetConfigTypes[]} [params.children] - Additional custom widgets to include in the tab.
 * @param {(data: any) => any} [params.onLoad] - Handler to manipulate data when the tab loads.
 * @param {(data: any) => any} [params.onSubmit] - Handler to manipulate data on form submission.
 * @returns {EditorTab} The configured editor tab object.
 */
export function buildEventTab(props, params) {
    const children = [];
    if (params === null || params === void 0 ? void 0 : params.eventOnLoad) {
        children.push(javaScriptTextField('onLoad', 'value, handler'));
    }
    if (params === null || params === void 0 ? void 0 : params.eventOnChange) {
        children.push(javaScriptTextField('onChange', 'value, handler'));
    }
    if ((params === null || params === void 0 ? void 0 : params.eventOnLoad) && (params === null || params === void 0 ? void 0 : params.eventOnChange)) {
        children.push({
            type: 'form.checkbox',
            key: 'event.onLoadCallOnChange',
            label: 'onLoad Call onChange',
        });
    }
    if (params === null || params === void 0 ? void 0 : params.eventOnChangeTab) {
        children.push(javaScriptTextField('onChangeTab', 'tab, handler'));
    }
    if (params === null || params === void 0 ? void 0 : params.eventOnClick) {
        children.push(javaScriptTextField('onClick', 'handler'));
    }
    if (params === null || params === void 0 ? void 0 : params.children) {
        children.push(...params.children);
    }
    if (params === null || params === void 0 ? void 0 : params.autoSave) {
        children.push({
            type: 'form.checkbox',
            key: 'autoSave',
            label: 'Auto Save',
            description: 'If checked then when user enters data in this component form will be automatically saved. For this to work form must be configured to support auto-save.',
        });
    }
    const onLoad = (data) => {
        if (params === null || params === void 0 ? void 0 : params.onLoad) {
            data = params.onLoad(data);
        }
        return data;
    };
    const onSubmit = (data) => {
        if (params === null || params === void 0 ? void 0 : params.onSubmit) {
            data = params.onSubmit(data);
        }
        return data;
    };
    return { label: 'Event', children, onLoad, onSubmit };
}
function javaScriptTextField(eventName, functionParams) {
    return {
        type: 'form.text',
        key: 'event.' + eventName,
        label: eventName + ' JavaScript Function',
        emptyAsNull: true,
        multiline: true,
        rows: 6,
        style: {
            monospace: true,
        },
        validate: {
            custom: (value) => {
                if (value) {
                    try {
                        Function('"use strict"; return (function(' + functionParams + ') { ' + value + '; })')();
                    }
                    catch (error) {
                        return getErrorMessage(error);
                    }
                }
                return undefined;
            },
        },
    };
}
